import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { formatNumber } from '../../lib/format.js';
import { Dialog, DialogContent } from '../../ui/Dialog.js';
import { Button } from '../../ui/Button.js';
import { Banner } from '../../ui/Banner.js';
import { Field } from '../../ui/Field.js';
import type { DataSummary, TenantSummary } from '../../lib/api.js';

/**
 * The last thing between an operator and a customer's books.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY TYPING THE SLUG, AND NOT AN "ARE YOU SURE" BUTTON
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A confirm button confirms that a button was clicked. It cannot distinguish
 * "yes, destroy Aurellia's data" from "yes, whatever this dialog said" — and
 * the second is what a person does at the end of a long afternoon.
 *
 * Typing the account's slug means the operator has to read which account this
 * is and reproduce it. It is the one interaction that cannot be completed
 * without attending to the subject.
 *
 * ── AND THE PLATFORM CHECKS IT AGAIN ──────────────────────────────────────
 *
 * This dialog compares what was typed against what it was handed. The platform
 * compares it against the slug IT holds for the tenant in scope, inside the
 * purge transaction. So a console showing a stale page — the account renamed,
 * the wrong row clicked, a tab left open while somebody else worked — cannot
 * confirm its way past the check: it would send a slug the platform disagrees
 * with, and get a 409 naming the account it actually has.
 *
 * The local check exists only to give a fast, quiet answer. The one that
 * matters is the far one.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT THIS DOES NOT DO, STATED ON THE SCREEN
 * ══════════════════════════════════════════════════════════════════════════
 *
 * It empties the books. It does NOT remove access: the tenant row, its
 * members, their roles and any role overrides all survive, so somebody who
 * could sign in this morning can sign in this afternoon to an empty account.
 *
 * That distinction was a bug once — role overrides were being cleared, which
 * quietly turned a data deletion into an access change. It is on the screen
 * now because an operator who expects a purge to lock somebody out will not
 * find out otherwise until they check.
 */
export function PurgeDialog({
  account,
  summary,
  onCancel,
  onConfirm,
}: {
  account: TenantSummary;
  summary: DataSummary;
  onCancel: () => void;
  onConfirm: (confirmSlug: string) => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [working, setWorking] = useState(false);

  /*
    Compared against the slug the PLATFORM returned for this account, not
    against anything the list page was showing. Trimmed, because a trailing
    space from a copy-paste is not a different account; not lowercased, because
    slugs are lowercase already and quietly accepting "AURELLIA" would be
    accepting something the operator did not actually read off the screen.
  */
  /*
    `summary.slug` is nullable on the wire, and that is not a formality: the
    summary is read under the tenant's own scope, so a null slug means the
    platform could not see the account it was asked about. Confirming against
    null would make every empty string a match.
  */
  const slug = summary.slug ?? account.slug;
  const matches = typed.trim() === slug;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        title={t('purge.title', { name: account.name })}
        description={t('purge.description')}
        width="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={working}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={!matches || working}
              onClick={() => {
                setWorking(true);
                void onConfirm(typed.trim()).finally(() => setWorking(false));
              }}
            >
              {working ? t('purge.working') : t('purge.confirm')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Banner intent="danger" title={t('purge.warningTitle')}>
            {t('purge.warningBody', {
              rows: formatNumber(summary.totalRows),
              events: formatNumber(summary.events),
              name: summary.name ?? account.name,
            })}
          </Banner>

          {/*
            What SURVIVES, said as plainly as what goes.

            An operator who believes this locks somebody out will not discover
            otherwise until they go looking. Being wrong in that direction is
            worse than being wrong the other way: they would leave a customer
            with access they meant to remove, and think it was done.
          */}
          <p className="text-md text-secondary">{t('purge.keeps')}</p>

          <Field
            label={t('purge.confirmLabel', { slug })}
            hint={t('purge.confirmHint')}
          >
            {/*
              A render prop, so the label, the hint and the input share one id
              and one `aria-describedby`. Wiring those by hand is how a field
              ends up describing an element that is not on the page.
            */}
            {(field) => (
              <input
                {...field}
                type="text"
                value={typed}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTyped(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              />
            )}
          </Field>
        </div>
      </DialogContent>
    </Dialog>
  );
}
